import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Target, Trophy, Plus, MoreVertical } from 'lucide-react';
import { format } from 'date-fns';
import { useLanguage } from '@/contexts/LanguageContext';
import { EditHabitDialog } from '@/components/customer/EditHabitDialog';
import { HabitPresetSelector } from '@/components/customer/HabitPresetSelector';
import { FrequencySelector } from '@/components/customer/FrequencySelector';
import { StepsLinkField } from '@/components/customer/StepsLinkField';
import type { Habit, HabitPreset, HabitFrequencyConfig } from '@/types/habits';
import { cn } from '@/lib/utils';
import { getBadgeForDays } from '@/lib/constants/habitBadges';
import { formatDateForArabic } from '@/lib/utils/dates';
import { enUS } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { deleteHabit, createHabit, getHabitDisplayName, getHabitCompletionCountOnDate, getHabitLogs } from '@/lib/firebase/queries/habits';
import { getUserHabits } from '@/lib/firebase/queries/habits';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { habitBadges } from '@/lib/constants/habitBadges';
import { calculateTotalDots } from '@/lib/utils/habitProgress';

interface CoachHabitsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName: string;
  customerNameAr?: string;
}

export function CoachHabitsDialog({ open, onOpenChange, customerId, customerName, customerNameAr }: CoachHabitsDialogProps) {
  const [habits, setHabits] = useState<Habit[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [habitToEdit, setHabitToEdit] = useState<Habit | null>(null);
  const [habitToDelete, setHabitToDelete] = useState<Habit | null>(null);
  const [newHabitName, setNewHabitName] = useState('');
  const [newHabitCategory, setNewHabitCategory] = useState<'Exercises' | 'Nutrition' | 'Sleep' | 'Stress' | 'Relations' | 'Addictions'>('Exercises');
  const [newHabitDescription, setNewHabitDescription] = useState<string | undefined>();
  const [newHabitDescriptionAr, setNewHabitDescriptionAr] = useState<string | undefined>();
  const [newHabitStepsTarget, setNewHabitStepsTarget] = useState<number | undefined>(undefined);
  const [isCreatingHabit, setIsCreatingHabit] = useState(false);
  const [habitProgressDots, setHabitProgressDots] = useState<Record<string, number[]>>({});
  const [frequencyConfig, setFrequencyConfig] = useState<HabitFrequencyConfig>({
    frequency: 'daily',
    timesPerFrequency: 1,
    duration: 90,
  });
  const { t, language, dir } = useLanguage();

  // Fetch customer habits using the same method as customer widget
  const fetchHabits = async () => {
    if (!customerId) return;
    
    try {
      setLoading(true);
      // Use the same function as the customer habits widget
      const userHabits = await getUserHabits(customerId);
      setHabits(userHabits);
    } catch (error) {
      console.error('Error fetching habits:', error);
      toast.error('Failed to load habits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && customerId) {
      fetchHabits();
    }
  }, [open, customerId]);

  // Load progress dots data for all habits
  useEffect(() => {
    let isMounted = true; // Track if component is still mounted
    
    const loadProgressDots = async () => {
      if (!customerId || habits.length === 0) return;
      
      try {
        const progressDotsMap: Record<string, number[]> = {};
      
      await Promise.all(
        habits.map(async (habit) => {
            try {
              const totalDots = calculateTotalDots(habit.frequencyConfig);
              const dots: number[] = Array(totalDots).fill(0);
          const startDate = new Date(habit.createdAt);
          startDate.setHours(0, 0, 0, 0);
          
              // OPTIMIZATION: Fetch ALL logs for this habit at once (1 query instead of 90!)
              const logs = await getHabitLogs(habit.id, customerId, habit.frequencyConfig.duration);
              
              // Create a map for fast lookup
              const logsMap = new Map(logs.map(log => [log.date, log]));
              
              // Process each day using the pre-fetched logs
              for (let i = 0; i < habit.frequencyConfig.duration && i < totalDots; i++) {
            const date = new Date(startDate);
            date.setDate(startDate.getDate() + i);
                const dateStr = format(date, 'yyyy-MM-dd');
                
                const log = logsMap.get(dateStr);
                // Handle both new (completionCount) and old (completed) log formats
                const completionCount = log?.completionCount ?? (log?.completed ? 1 : 0);
                
                // Dot is filled only when target is fully met
                dots[i] = completionCount >= habit.frequencyConfig.timesPerFrequency ? 1 : 0;
          }
          
          progressDotsMap[habit.id] = dots;
            } catch (habitError) {
              console.error(`Error processing habit:`, habitError);
              // Set empty dots on error
              progressDotsMap[habit.id] = Array(calculateTotalDots(habit.frequencyConfig)).fill(0);
            }
        })
      );
      
        // Only update state if component is still mounted
        if (isMounted) {
      setHabitProgressDots(progressDotsMap);
        }
      } catch (error) {
        console.error('Error loading progress dots:', error);
      }
    };

    loadProgressDots();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [customerId, habits.map(h => h.id).join(',')]); // Reload when habit IDs change

  // Transient add-habit dialog state must not survive a close — cancel, the
  // X button, overlay click, and Escape all route through this Dialog's
  // onOpenChange, so resetting there (rather than only on successful create)
  // covers every dismissal path. A leaked stepsTarget in particular would
  // silently make the next unrelated habit auto-complete by steps.
  const resetAddHabitState = () => {
    setNewHabitName('');
    setNewHabitCategory('Exercises');
    setNewHabitDescription(undefined);
    setNewHabitDescriptionAr(undefined);
    setNewHabitStepsTarget(undefined);
    setFrequencyConfig({
      frequency: 'daily',
      timesPerFrequency: 1,
      duration: 90,
    });
  };

  const handleAddDialogOpenChange = (next: boolean) => {
    if (!next) {
      resetAddHabitState();
    }
    setShowAddDialog(next);
  };

  const handleCreateHabit = async () => {
    if (!newHabitName.trim()) {
      toast.error('Please enter a habit name');
      return;
    }
    
    try {
      setIsCreatingHabit(true);
      
      // Automatically select the first bronze badge for the chosen category
      const defaultBadge = habitBadges[newHabitCategory].badges.bronze[0].id;
      
      await createHabit({
        name: newHabitName.trim(),
        category: newHabitCategory,
        badge: defaultBadge,
        userId: customerId,
        startDate: new Date(),
        frequencyConfig,
        description: newHabitDescription,
        descriptionAr: newHabitDescriptionAr,
        stepsTarget: newHabitStepsTarget,
      });

      await fetchHabits();
      toast.success('Habit created successfully');
      handleAddDialogOpenChange(false);
    } catch (error) {
      console.error('Error creating habit:', error);
      toast.error('Failed to create habit');
    } finally {
      setIsCreatingHabit(false);
    }
  };

  const handlePresetSelect = (preset: HabitPreset) => {
    setNewHabitName(language === 'ar' && preset.nameAr ? preset.nameAr : preset.name);
    setNewHabitCategory(preset.category);
    setNewHabitDescription(preset.description);
    setNewHabitDescriptionAr(preset.descriptionAr);
    // If preset has frequency config, use it
    if (preset.frequencyConfig) {
      setFrequencyConfig(preset.frequencyConfig);
    }
  };

  const handleDeleteHabit = async () => {
    if (!habitToDelete) return;
    
    try {
      await deleteHabit(habitToDelete.id);
      await fetchHabits();
      toast.success('Habit deleted successfully');
      setHabitToDelete(null);
    } catch (error) {
      console.error('Error deleting habit:', error);
      toast.error('Failed to delete habit');
    }
  };


  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh]" dir={dir}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", dir === 'rtl' ? 'justify-end text-right flex-row-reverse' : 'text-left')} dir={dir}>
              {language === 'ar' ? (
                <>
                  {`عادات ${customerNameAr || customerName}`}
                  <Target className="h-5 w-5" />
                </>
              ) : (
                <>
                  <Target className="h-5 w-5" />
                  {`${customerName}'s Habits`}
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className={cn("flex justify-start items-center mb-4")}>
            <Button
              onClick={() => setShowAddDialog(true)}
              size="sm"
              className="bg-[#fbbf24] hover:bg-[#f59e0b] text-white"
            >
              <Plus className={cn("h-4 w-4", dir === 'rtl' ? 'ml-2' : 'mr-2')} />
              {language === 'ar' ? 'إضافة عادة' : 'Add Habit'}
            </Button>
          </div>

          <ScrollArea className="h-[60vh] px-6" dir={dir}>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : habits.length === 0 ? (
              <div className="text-center py-8">
                <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  {language === 'ar' ? 'لا توجد عادات بعد' : 'No habits yet'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {language === 'ar' 
                    ? `ابدأ بإضافة العادة الأولى لـ ${customerNameAr || customerName}`
                    : `Start by adding the first habit for ${customerName}`
                  }
                </p>
                <Button
                  onClick={() => setShowAddDialog(true)}
                  className="bg-[#fbbf24] hover:bg-[#f59e0b] text-white"
                >
                  <Plus className={cn("h-4 w-4", dir === 'rtl' ? 'ml-2' : 'mr-2')} />
                  {language === 'ar' ? 'إضافة العادة الأولى' : 'Add First Habit'}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {habits.map((habit, index) => {
                  const selectedDate = new Date(); // Always show today's status for coach
                  const badge = getBadgeForDays(habit.category, habit.daysCompleted, t);

                  return (
                    <motion.div
                      key={habit.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="space-y-2 border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="space-y-1">
                          {/* Mobile: Name wraps with 3-dot menu on the same row */}
                          <div className="sm:hidden">
                            <div className="grid grid-cols-[1fr,auto] gap-2 mb-2">
                              <span className="font-medium whitespace-normal break-words">{getHabitDisplayName(habit, null, t)}</span>
                              <div className="justify-self-end">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align={dir === 'rtl' ? 'start' : 'end'}>
                                    <DropdownMenuItem onClick={() => setHabitToEdit(habit)}>
                                      {language === 'ar' ? 'تعديل' : 'Edit'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => setHabitToDelete(habit)}
                                      className="text-red-600"
                                    >
                                      {language === 'ar' ? 'حذف' : 'Delete'}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          </div>

                          {/* Desktop: Original habit name and badges */}
                          <div className="hidden sm:flex items-center gap-2">
                            <span className="font-medium">{getHabitDisplayName(habit, null, t)}</span>
                            <Badge variant="secondary" className="text-xs">
                              <Trophy className="h-3 w-3 mr-1" />
                              {language === 'ar' 
                                ? `${habit.streak} يوم متتالي`
                                : `${habit.streak} day streak`
                              }
                            </Badge>
                          </div>
                          
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <Badge variant="outline" className="font-normal">
                                {language === 'ar' ? badge.name : badge.name}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                {language === 'ar' 
                                  ? `بدأت في ${formatDateForArabic(new Date(habit.createdAt))}`
                                  : `Started on ${format(new Date(habit.createdAt), 'PP', { locale: enUS })}`
                                }
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Desktop: Original button layout */}
                        <div className="hidden sm:flex items-center justify-end gap-2 w-full sm:w-auto">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align={dir === 'rtl' ? 'start' : 'end'}>
                              <DropdownMenuItem onClick={() => setHabitToEdit(habit)}>
                                {language === 'ar' ? 'تعديل' : 'Edit'}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => setHabitToDelete(habit)}
                                className="text-red-600"
                              >
                                {language === 'ar' ? 'حذف' : 'Delete'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>{language === 'ar' ? 'التقدم' : 'Progress'}</span>
                          <span>
                            {(() => {
                              const startDate = new Date(habit.createdAt);
                              startDate.setHours(0, 0, 0, 0);
                              const selectedDateStart = new Date(selectedDate);
                              selectedDateStart.setHours(0, 0, 0, 0);
                              const daysSinceCreation = Math.floor((selectedDateStart.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                              if (daysSinceCreation >= 90) {
                                return language === 'ar' ? 'مكتمل' : 'Finished';
                              }
                              return language === 'ar' 
                                ? `اليوم ${Math.max(1, daysSinceCreation + 1)}`
                                : `Day ${Math.max(1, daysSinceCreation + 1)}`;
                            })()}
                          </span>
                        </div>
                        {/* Segmented Progress Bar */}
                        <div className="flex h-2 w-full rounded-full overflow-hidden bg-muted/30">
                          {(() => {
                            const totalDots = calculateTotalDots(habit.frequencyConfig);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const startDate = new Date(habit.createdAt);
                            startDate.setHours(0, 0, 0, 0);
                            
                            return Array.from({ length: totalDots }).map((_, i) => {
                              const isCompleted = (habitProgressDots[habit.id]?.[i] || 0) === 1;
                              const segmentDate = new Date(startDate);
                              segmentDate.setDate(startDate.getDate() + i);
                              segmentDate.setHours(0, 0, 0, 0);
                              const isPast = segmentDate < today;

                              return (
                                <div
                                  key={i}
                                  className={cn(
                                    "flex-1 h-full",
                                    isCompleted
                                      ? 'bg-[#7dda55]' // Namat green for completed
                                      : isPast
                                        ? 'bg-[#7dda55]/25' // Light green for missed past days
                                        : 'bg-muted/30' // Light gray for future days
                                  )}
                                />
                              );
                            });
                          })()}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Add Habit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={handleAddDialogOpenChange}>
        <DialogContent className={`${showPresets ? 'max-w-5xl' : 'max-w-md'} max-h-[90vh] overflow-hidden flex flex-col`} dir={dir}>
          <DialogHeader>
            <DialogTitle className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {language === 'ar' ? `إضافة عادة جديدة لـ ${customerNameAr || customerName}` : `Add New Habit for ${customerName}`}
            </DialogTitle>
          </DialogHeader>
          
          {/* Two-column layout with scrolling - grows to fill space */}
          <div className="flex flex-col md:flex-row gap-4 flex-1 min-h-0 overflow-y-auto">
            {/* Left Column: Form - flexible width when presets shown */}
            <div className={`${showPresets ? 'md:flex-[2]' : 'w-full'} min-w-0 flex flex-col`}>
              <div className="space-y-3 flex-1">
            <div>
                  <Label htmlFor="habit-name" className={`text-sm font-medium ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                {language === 'ar' ? 'اسم العادة' : 'Habit Name'}
              </Label>
              <Input
                id="habit-name"
                value={newHabitName}
                onChange={(e) => setNewHabitName(e.target.value)}
                placeholder={language === 'ar' ? 'أدخل اسم العادة...' : 'Enter habit name...'}
                    className={cn("mt-1 h-10", dir === 'rtl' ? 'text-right' : 'text-left')}
                dir={dir}
              />
            </div>

            <StepsLinkField value={newHabitStepsTarget} onChange={setNewHabitStepsTarget} />

            <div>
                  <Label htmlFor="habit-category" className={`text-sm font-medium ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                {language === 'ar' ? 'الفئة' : 'Category'}
              </Label>
              <Select value={newHabitCategory} onValueChange={(value: any) => setNewHabitCategory(value)} dir={dir}>
                    <SelectTrigger className={cn("mt-1 h-10", dir === 'rtl' ? 'text-right' : 'text-left')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Exercises">{language === 'ar' ? 'التمارين' : 'Exercises'}</SelectItem>
                  <SelectItem value="Nutrition">{language === 'ar' ? 'التغذية' : 'Nutrition'}</SelectItem>
                  <SelectItem value="Sleep">{language === 'ar' ? 'النوم' : 'Sleep'}</SelectItem>
                  <SelectItem value="Stress">{language === 'ar' ? 'التوتر' : 'Stress'}</SelectItem>
                  <SelectItem value="Relations">{language === 'ar' ? 'العلاقات' : 'Relations'}</SelectItem>
                  <SelectItem value="Addictions">{language === 'ar' ? 'الإدمان' : 'Addictions'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
                {/* Frequency Configuration */}
                <FrequencySelector
                  value={frequencyConfig}
                  onChange={setFrequencyConfig}
                  disabled={isCreatingHabit}
                />
              </div>
                
              {/* Buttons fixed at bottom of left column */}
              <div className={cn("flex gap-2 pt-4 mt-auto", dir === 'rtl' ? 'justify-start flex-row-reverse' : 'justify-end')}>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowPresets(!showPresets)}
                    className="h-10 text-sm bg-[#004250] hover:bg-[#003240] text-white border-[#004250] hover:border-[#003240]"
                  >
                    {showPresets 
                      ? (language === 'ar' ? 'إخفاء العادات الجاهزة' : 'Hide Presets')
                      : (language === 'ar' ? 'استخدام عادات جاهزة' : 'Use Ready Habits')
                    }
                  </Button>
                  <Button variant="outline" onClick={() => handleAddDialogOpenChange(false)} className="h-10 text-sm">
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button 
                onClick={handleCreateHabit}
                disabled={isCreatingHabit || !newHabitName.trim()}
                    className="bg-[#fbbf24] hover:bg-[#f59e0b] text-white h-10 text-sm"
              >
                {isCreatingHabit ? (
                  <>
                        <div className={cn("animate-spin rounded-full h-3 w-3 border-b-2 border-white", dir === 'rtl' ? 'ml-2' : 'mr-2')}></div>
                    {language === 'ar' ? 'جاري الإنشاء...' : 'Creating...'}
                  </>
                ) : (
                  language === 'ar' ? 'إنشاء العادة' : 'Create Habit'
                )}
              </Button>
            </div>
            </div>

            {/* Right Column: Presets - flexible width */}
            {showPresets && (
              <div className="md:flex-[3] min-w-0 flex flex-col border-t md:border-t-0 md:border-l pt-4 md:pt-0 md:pl-4 overflow-hidden bg-muted/70 rounded-lg p-4 md:p-3">
                <div className="flex-1 min-h-0">
                  <HabitPresetSelector onSelectPreset={handlePresetSelect} />
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Habit Dialog */}
      {habitToEdit && (
        <EditHabitDialog
          habit={habitToEdit}
          open={!!habitToEdit}
          onOpenChange={(open) => !open && setHabitToEdit(null)}
          onHabitUpdated={fetchHabits}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!habitToDelete} onOpenChange={(open) => !open && setHabitToDelete(null)}>
        <AlertDialogContent dir={dir}>
          <AlertDialogHeader>
            <AlertDialogTitle className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {language === 'ar' ? 'حذف العادة' : 'Delete Habit'}
            </AlertDialogTitle>
            <AlertDialogDescription className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {language === 'ar' 
                ? `هل أنت متأكد من حذف "${habitToDelete?.name}"؟ لا يمكن التراجع عن هذا الإجراء.`
                : `Are you sure you want to delete "${habitToDelete?.name}"? This action cannot be undone.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={cn("gap-2", dir === 'rtl' ? 'flex-row-reverse' : '')}>
            <AlertDialogCancel>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteHabit} className="bg-destructive hover:bg-destructive/90">
              {language === 'ar' ? 'حذف' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
